import { createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { AddCircle, CheckCircle, Edit, Delete, Cancel } from '@material-ui/icons';
import { booleanOptions, BooleanValue, percentualAmortizacaoOptions, modalidadeAmortizacaoOptions } from 'common/genericOptions';
import { Amortizacao, AmortizacaoViewModel, ModalidadeAmortizacao } from 'common/genericTypes';
import { Button, Dropdown, MdSpace, RadioButton, Section, SmSpace } from 'm2m.components';
import moment, { Moment } from 'moment';
import React, { useEffect } from 'react';
import { Field } from 'store/modules/notaComercial/eventos';
import { v4 as uuidv4 } from 'uuid';
import { labelsEventosFinanceiros as labels } from 'common/registro/labels';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { localizedTextsMap } from 'utils/dataGridUtils';
import { formatNumber } from 'utils/number';
import {
  DataGridPro,
  GridToolbarContainer,
  GridRowModesModel,
  GridRowsProp,
  GridRowModes,
  GridRowParams,
  MuiEvent,
  GridEventListener,
  GridRowModel,
  GridRowId,
  GridActionsCellItem,
  GridColumns,
  GridValueFormatterParams,
  GridSelectionModel,
  GridCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { EtapaCadastro } from 'common/registro/enums';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dataGridDiv: {
      height: 400,
      width: 700,
      '& .amortizacaoError': {
        backgroundColor: theme.palette.error.main,
      },
    },
  })
);


export interface AmortizacaoSectionProps {
  percentualAmortizacaoSobre: string;
  percentualAmortizacaoPassivelAlteracao: boolean;
  modalidadeAmortizacao?: string;
  onUpdateField: (field: Field) => void;
  onUpdateAmortizacoes: (amortizacoesViewModel: AmortizacaoViewModel[]) => void;
  useValidarCampos: (arg?: EtapaCadastro) => Array<string>;
  amortizacoes: Amortizacao[];
  minDate?: Moment;
  maxDate?: Moment;
  shouldHighlightErrors: boolean;
}

export const AmortizacoesSection: React.FC<AmortizacaoSectionProps> = (props) => {
  const {
    onUpdateAmortizacoes,
    percentualAmortizacaoSobre,
    percentualAmortizacaoPassivelAlteracao,
    modalidadeAmortizacao,
    onUpdateField,
    amortizacoes,
    minDate,
    maxDate,
    useValidarCampos,
    shouldHighlightErrors
  } = props;
  const classes = useStyles();

  const dispatch = useDispatch();
  const getValidarDiaUtil = useSelector((state: RootState) => state.feriadosNacionais.getValidarDiaUtil);

  const highlightErrors = useSelector((state: RootState) => shouldHighlightErrors);
  const camposInvalidos = useValidarCampos(EtapaCadastro.EventosFinanceiros);

  const [pageSize, setPageSize] = React.useState(20);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const [selectionModel, setSelectionModel] = React.useState<GridSelectionModel>([]);
  const [rows, setRows] = React.useState<GridRowsProp>([]);
  const [forceUpdate, setForceUpdate] = React.useState<number>(0);
  const [invalidDates, setInvalidDates] = React.useState<Record<string, boolean>>({});

  const showError = (label: string) => camposInvalidos.includes(label) && highlightErrors;

  const updateForceUpdate = () => {
    setTimeout(() => {
      setForceUpdate((prev) => prev + 1);
    }, 0);
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    const updatedRows = rows.filter((row) => row.id !== id);
    setRows(updatedRows);
    handleRowsChange(updatedRows);
    updateForceUpdate();
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      const updatedRows = rows.filter((row) => row.id !== id);
      setRows(updatedRows);
      handleRowsChange(updatedRows);
    }
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  interface EditToolbarProps {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (newModel: (oldModel: GridRowModesModel) => GridRowModesModel) => void;
  }

  function EditToolbar(props: EditToolbarProps) {
    const handleClick = () => {
      const id = uuidv4();
      setRows([...rows, { id, percentual: null, dataAmortizacao: null, isNew: true }]);
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'percentual' } });

      updateForceUpdate();
    };

    const handleDeleteClick = () => {
      const rowsUpdated = rows.filter((row) => !selectionModel.some((sm) => sm === row.id));
      setRows(rowsUpdated);
      handleRowsChange(rowsUpdated);

      updateForceUpdate();
    };

    return (
      <GridToolbarContainer>
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={handleClick}
          startIcon={<AddCircle fontSize="small" />}
        >
          {'Nova amortização'}
        </Button>
        <Button
          color="error"
          size="small"
          onClick={handleDeleteClick}
          disabled={!selectionModel.length}
          startIcon={<Delete fontSize="small" />}
        >
          {'Excluir amortizações'}
        </Button>
      </GridToolbarContainer>
    );
  }

  const handleRowEditStart = (params: GridRowParams, event: MuiEvent<React.SyntheticEvent>) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    const updatedRows = rows.map((row) => (row.id === newRow.id ? updatedRow : row));
    setRows(updatedRows);

    updateForceUpdate();

    handleRowsChange(updatedRows);
    return updatedRow;
  };

  const handleRowsChange = (newRows: GridValidRowModel[]) => {
    const amortizacaoNew = newRows
      .filter((r) => r.dataAmortizacao && r.percentual)
      .map((x) => {
        const amortizacao = {
          id: x.id,
          dataAmortizacao: x.dataAmortizacao,
          percentualAmortizacao: x.percentual,
        } as AmortizacaoViewModel;
        return amortizacao;
      });

    onUpdateAmortizacoes(amortizacaoNew);
  };

  const isDataAmortizacaoValid = async (date: Date) => {
    const momentDate = moment(date);

    if (!!minDate && momentDate.isBefore(minDate, 'day')) return false;
    if (!!maxDate && momentDate.isAfter(maxDate, 'day')) return false;

    const response = await getValidarDiaUtil(dispatch, date, false);
    if (!response.diaUtil) return false;

    return true;
  };

  useEffect(() => {
    const validateDates = async () => {
      const newInvalidDates: Record<string, boolean> = {};

      for (const row of rows) {
        if (row.dataAmortizacao) {
          const isValid = await isDataAmortizacaoValid(row.dataAmortizacao);
          if (!isValid) {
            newInvalidDates[row.id] = true;
          }
        }
      }

      setInvalidDates(newInvalidDates);
    };

    validateDates();
  }, [rows, minDate, maxDate]);

  const getGridColumns: GridColumns = [
    {
      field: 'percentual',
      headerName: '% da Amortização',
      type: 'number',
      width: 180,
      editable: true,
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        return !params.value ? '' : formatNumber(params.value, { minimumFractionDigits: 8, maximumFractionDigits: 8 });
      },
    },
    {
      field: 'dataAmortizacao',
      headerName: 'Data da Amortização',
      width: 250,
      type: 'date',
      editable: true,
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        return !params.value ? '' : moment(params.value).format('DD/MM/YYYY');
      },
      cellClassName: (params: GridCellParams<Date>) => {
        return invalidDates[params.id] ? 'amortizacaoError' : '';
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Ações',
      width: 200,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<CheckCircle fontSize="small" />}
              label="Save"
              className="textPrimary"
              onClick={handleSaveClick(id)}
              color="inherit"
              onResize={() => {}}
              onResizeCapture={() => {}}
            />,
            <GridActionsCellItem
              icon={<Cancel fontSize="small" />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
              onResize={() => {}}
              onResizeCapture={() => {}}
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<Edit fontSize="small" />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
            onResize={() => {}}
            onResizeCapture={() => {}}
          />,
          <GridActionsCellItem
            icon={<Delete fontSize="small" />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
            onResize={() => {}}
            onResizeCapture={() => {}}
          />,
        ];
      },
    },
  ];

  useEffect(() => {
    if (amortizacoes.length) {
      setRows(
        amortizacoes.map((amortizacao) => ({
          id: uuidv4(),
          percentual: amortizacao.percentualAmortizacao,
          dataAmortizacao: amortizacao.dataAmortizacao,
          isNew: false,
        }))
      );
    }
  }, [amortizacoes]);

  return (
    <>
      <Section title={labels.pagamentoAmortizacao}>
        <Grid container spacing={2} style={{ width: '100%' }}>
          <Grid item xs={12}>
            <RadioButton
              label={labels.percentualAmortizacaoSobre}
              options={percentualAmortizacaoOptions}
              value={percentualAmortizacaoSobre ?? ''}
              onChange={(value: string) =>
                onUpdateField({
                  name: 'percentualAmortizacaoSobre',
                  value,
                })
              }
              inline
            />
            <SmSpace />
            <RadioButton
              label={labels.percentualAmortizacaoPassivelAlteracao}
              options={booleanOptions}
              value={percentualAmortizacaoPassivelAlteracao ? BooleanValue.Yes : BooleanValue.No}
              onChange={(value: string) =>
                onUpdateField({
                  name: 'percentualAmortizacaoPassivelAlteracao',
                  value: value === BooleanValue.Yes,
                })
              }
              inline
            />
            <SmSpace />
            <RadioButton
              label={labels.modalidadeAmortizacao}
              options={modalidadeAmortizacaoOptions}
              value={modalidadeAmortizacao ?? ModalidadeAmortizacao.CUSTOMIZADO}
              onChange={(value: string) =>
                onUpdateField({
                  name: 'modalidadeAmortizacao',
                  value: value as ModalidadeAmortizacao,
                })
              }
              inline
            />
            <MdSpace />
            <Typography variant="subtitle1">{'Amortizações'}</Typography>
            <SmSpace />
            <div className={classes.dataGridDiv}>
              <DataGridPro
                rows={rows}
                columns={getGridColumns}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
                onRowEditStart={handleRowEditStart}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                components={{
                  Toolbar: EditToolbar,
                }}
                componentsProps={{ toolbar: { setRows, setRowModesModel } }}
                experimentalFeatures={{ newEditingApi: true }}
                pageSize={pageSize}
                checkboxSelection
                disableSelectionOnClick
                onSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
                selectionModel={selectionModel}
                localeText={localizedTextsMap}
              />
            </div>
          </Grid>
        </Grid>
      </Section>
    </>
  );
};
